import React, { useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getModuleByPath } from '../config/moduleConfig';
import ModuleSidebar from './ModuleSidebar';
import Header from '../components/shared/Header';
import { SidebarProvider } from '../context/SidebarContext';
import { useAuth } from '../context/AuthContext';
import useBranch from '../hooks/useBranch';

/**
 * ModuleLayout Component
 *
 * PERMISSION GUARD — runs on EVERY navigation, not just entry:
 *
 *   1. Flatten all menu items (parents + children) into a permission map
 *      keyed by path.
 *
 *   2. If the current path is in the map and the user lacks the required
 *      permission → redirect to the first accessible route in this module.
 *
 *   3. If the user has no accessible routes at all → /select-role.
 *
 * This prevents the "Access Denied" screen from showing when the user
 * navigates directly to a URL they don't have permission for.
 */
export default function ModuleLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { restaurantName } = useBranch();
  const { hasPermission } = useAuth();

  // Detect current module from URL path
  const currentModule = useMemo(
    () => getModuleByPath(location.pathname),
    [location.pathname]
  );

  // ── Build a flat path→permission map from all menu items ──────────────────
  const permissionMap = useMemo(() => {
    const map = {}; // { '/inventory/items': 'inventory.items', ... }
    if (!currentModule?.menuItems) return map;

    currentModule.menuItems.forEach((item) => {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      if (hasChildren) {
        item.children.forEach((child) => {
          if (child.path && child.permission) map[child.path] = child.permission;
        });
      } else {
        if (item.path && item.permission) map[item.path] = item.permission;
      }
    });

    return map;
  }, [currentModule]);

  // ── Find first accessible route in the module ──────────────────────────────
  const firstAccessibleRoute = useMemo(() => {
    if (!currentModule?.menuItems?.length) return null;

    for (const item of currentModule.menuItems) {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      if (hasChildren) {
        const child = item.children.find(
          (c) => !c.permission || hasPermission(c.permission)
        );
        if (child) return child.path;
      } else {
        if (!item.permission || hasPermission(item.permission)) return item.path;
      }
    }

    return null; // nothing accessible
  }, [currentModule, hasPermission]);

  // ── Guard: check permission for current path on every navigation ───────────
  useEffect(() => {
    if (!currentModule) return;

    const requiredPermission = permissionMap[location.pathname];

    // Path is not in the map (no permission guard) — let it through
    if (!requiredPermission) return;

    // Path is guarded — check if user has permission
    if (!hasPermission(requiredPermission)) {
      if (firstAccessibleRoute) {
        navigate(firstAccessibleRoute, { replace: true });
      } else {
        navigate('/select-role', { replace: true });
      }
    }
  }, [location.pathname, permissionMap, hasPermission, firstAccessibleRoute, currentModule]);

  // If no module found, show error
  if (!currentModule) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Module Not Found</h1>
          <p className="text-gray-600 mb-4">The requested module could not be found.</p>
          <button
            onClick={() => navigate('/select-role')}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
        <Header restaurantName={restaurantName} />

        <div className="flex flex-1 overflow-hidden">
          <ModuleSidebar module={currentModule} />

          <main className="flex-1 overflow-auto bg-gray-50">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}